/**
 * API Client for Green Filter
 * Handles all API requests to backend and Shopify
 * Uses native fetch API for better performance and smaller bundle size
 */

// API Configuration
const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'https://greenfilter-api.codingtier.com';

interface ApiResponse<T = any> {
  ResponseCode: string;
  data?: {
    rows?: T[];
    count?: number;
  };
  message?: string;
}

/**
 * Generic GET request handler with native fetch
 * Timeout reduced from 200s to 30s for better performance
 */
export async function getData<T = any>(
  endpoint: string,
  timeout: number = 30000,
  revalidateSeconds?: number
): Promise<ApiResponse<T>> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
      // Default: always fetch fresh data (no-store). Callers that are fine with
      // (and need to be compatible with) route-level static/ISR caching - e.g.
      // sitemap.ts, which declares `revalidate` - pass revalidateSeconds instead;
      // mixing a no-store fetch with a statically-rendered route is a hard
      // Next.js build error ("Dynamic server usage"), since the two conflict.
      ...(revalidateSeconds !== undefined
        ? { next: { revalidate: revalidateSeconds } }
        : { cache: 'no-store' }),
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse<T> = await response.json();
    return data;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error && error.name === 'AbortError') {
      console.error('API Error: Request timeout');
      return {
        ResponseCode: 'Error',
        message: 'Request timeout',
      };
    }

    console.error('API Error:', error);
    return {
      ResponseCode: 'Error',
      message: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Fetch product by part number or product ID
 */
export async function getProduct(params: {
  gfu_part_num?: string;
  product_id?: string;
}) {
  // Ensure we have at least one parameter
  if (!params.gfu_part_num && !params.product_id) {
    console.error('getProduct called without gfu_part_num or product_id');
    return null;
  }

  const queryParam = params.gfu_part_num
    ? `gfu_part_num=${params.gfu_part_num}`
    : `product_id=${params.product_id}`;

  const response = await getData(`/product?${queryParam}`);

  if (response.ResponseCode === 'Success' && response.data?.rows) {
    return response.data.rows[0] || null;
  }

  return null;
}

/**
 * Fetch make/brand data with models and products
 */
export async function getMakeBySlug(slug: string, revalidateSeconds?: number): Promise<any> {
  const response = await getData(`/make?slug=${slug}`, undefined, revalidateSeconds);

  // Distinguish "request failed" (throw) from "no such make" (null) so the
  // brand page can return a 404 only for slugs the API doesn't know. The API
  // answers an unknown slug with HTTP 404, which getData reports as an Error.
  if (response.ResponseCode === 'Error' && !response.message?.includes('status: 404')) {
    throw new Error(response.message || 'Failed to fetch make');
  }

  if (response.ResponseCode === 'Success' && response.data) {
    // For make endpoint, data is the object directly, not wrapped in rows
    return response.data;
  }

  return null;
}

/**
 * Fetch all products (with pagination)
 */
export async function getProducts(
  pageNo: number = 1,
  pageSize: number = 50,
  revalidateSeconds?: number
) {
  const response = await getData(
    `/product-search?page=${pageNo}&limit=${pageSize}`,
    30000,
    revalidateSeconds
  );

  if (response.ResponseCode === 'Success' && response.data?.rows) {
    return {
      products: response.data.rows,
      count: response.data.count || 0,
    };
  }

  return {
    products: [],
    count: 0,
  };
}

export interface BrandLink {
  title: string;
  slug: string;
}

export interface BrandGroup {
  id: number;
  title: string;
  brands: BrandLink[];
}

// The curated make collections shown on /shop-by-vehicle, in display order.
const BRAND_COLLECTIONS = [
  { id: 5, title: 'Popular Brands' },
  { id: 1, title: 'High Performance' },
  { id: 2, title: 'Popular Pickup Trucks' },
  { id: 4, title: 'Insane Supercars' },
];

/**
 * Fetch the brand hub directory (/brand/{slug} pages), grouped the same way
 * as on /shop-by-vehicle. Server-side and ISR-cacheable, so pages can render
 * the brand links into their initial HTML and the sitemap can list them.
 * A collection that fails to load is skipped rather than failing the page.
 */
export async function getBrandDirectory(revalidateSeconds: number = 3600): Promise<BrandGroup[]> {
  const groups = await Promise.all(
    BRAND_COLLECTIONS.map(async ({ id, title }) => {
      const response = await getData<{ makes?: BrandLink[] }>(`/collection?id=${id}`, undefined, revalidateSeconds);
      const makes = response.ResponseCode === 'Success' ? response.data?.rows?.[0]?.makes ?? [] : [];
      return {
        id,
        title,
        brands: makes
          .filter((make) => make.slug && make.title)
          .map((make) => ({ title: make.title, slug: make.slug }))
          .sort((a, b) => a.title.localeCompare(b.title)),
      };
    })
  );
  return groups.filter((group) => group.brands.length > 0);
}

/**
 * Fetch all competitor companies for the cross-reference tool
 * Uses ISR-compatible caching (not getData's no-store) so the page
 * stays statically prerendered and revalidates hourly instead of
 * becoming fully dynamic on every request.
 */
export async function getCompetitors() {
  try {
    const response = await fetch(`${API_BASE_URL}/competitor?pageSize=200`, {
      headers: { 'Content-Type': 'application/json' },
      next: { revalidate: 3600 },
    });

    if (!response.ok) {
      return [];
    }

    const data: ApiResponse = await response.json();

    if (data.ResponseCode === 'Success' && data.data?.rows) {
      return data.data.rows;
    }

    return [];
  } catch (error) {
    console.error('API Error:', error);
    return [];
  }
}

/**
 * Fetch global data (banners, settings, etc.)
 */
export async function getGlobalData() {
  const response = await getData('/global-settings');

  if (response.ResponseCode === 'Success') {
    return response.data || {};
  }

  return {};
}

/**
 * Search products
 */
export async function searchProducts(query: string) {
  const response = await getData(`/products/search?q=${encodeURIComponent(query)}`);

  if (response.ResponseCode === 'Success' && response.data?.rows) {
    return response.data.rows;
  }

  return [];
}

